/**
 * Hook para buscar estatísticas do funil do fluxo e manter atualizado em tempo real
 * Consome a RPC get_flow_funnel_stats e faz subscription em flow_execution_steps
 * 
 * @release v1.0.0 - 2026-03-07
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('useFlowFunnelStats');

/** Estatísticas por node do funil */
export interface FunnelNodeStats {
  node_id: string;
  entries: number;
  exits: number;
  drop_offs: number;
  avg_time_seconds: number | null;
  /** Percentual relativo ao node Start (primeiro node) */
  funnel_percentage: number;
}

/** Estatísticas por transição (edge) do funil */
export interface FunnelEdgeStats {
  source_node_id: string;
  target_node_id: string;
  transitions: number;
  /** Percentual relativo ao total de entries do node de origem */
  percentage: number;
}

/** Resposta completa do funil */
export interface FlowFunnelData {
  node_stats: FunnelNodeStats[];
  edge_stats: FunnelEdgeStats[];
}

/**
 * Hook principal para estatísticas do funil em tempo real
 * 
 * @param flowId - ID do fluxo
 * @param period - Período de filtro (ex: '24h', '7d', '30d')
 * @param enabled - Habilitar/desabilitar a query
 */
export function useFlowFunnelStats(
  flowId: string | undefined,
  period: string = '7d',
  enabled: boolean = true,
) {
  const queryClient = useQueryClient();
  const queryKey = ['flow-funnel-stats', flowId, period];

  // Busca estatísticas via RPC
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!flowId) throw new Error('flowId obrigatório');

      log.info('Buscando estatísticas do funil', { flowId, period });

      // RPC será criada via migration — cast necessário até regenerar types
      const { data, error } = await (supabase.rpc as any)('get_flow_funnel_stats', {
        p_flow_id: flowId,
        p_period: period,
      });

      if (error) {
        log.error('Erro ao buscar estatísticas do funil', { error: error.message });
        throw error;
      }

      // A RPC retorna JSON com node_stats e edge_stats
      const result = data as unknown as FlowFunnelData;
      log.debug('Estatísticas do funil recebidas', {
        nodes: result?.node_stats?.length || 0,
        edges: result?.edge_stats?.length || 0,
      });

      return result;
    },
    enabled: !!flowId && enabled,
    refetchInterval: 30000, // Fallback: refetch a cada 30s para consistência
    staleTime: 10000,
  });

  // Subscription em tempo real para flow_execution_steps
  useEffect(() => {
    if (!flowId || !enabled) return;

    log.info('Inscrevendo em atualizações realtime do funil', { flowId });

    const channel = supabase
      .channel(`funnel-${flowId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'flow_execution_steps',
        },
        (payload) => {
          log.debug('Novo step detectado via realtime', { nodeId: payload.new?.node_id });
          // Invalida cache para refetch
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      log.info('Desinscrevendo do realtime do funil');
      supabase.removeChannel(channel);
    };
  }, [flowId, enabled, queryClient, queryKey]);

  /**
   * Obtém estatísticas de um node específico
   */
  const getNodeStats = useCallback(
    (nodeId: string): FunnelNodeStats | undefined => {
      return query.data?.node_stats?.find((n) => n.node_id === nodeId);
    },
    [query.data],
  );

  /**
   * Obtém estatísticas de uma edge específica (source → target)
   */
  const getEdgeStats = useCallback(
    (sourceId: string, targetId: string): FunnelEdgeStats | undefined => {
      return query.data?.edge_stats?.find(
        (e) => e.source_node_id === sourceId && e.target_node_id === targetId,
      );
    },
    [query.data],
  );

  return {
    ...query,
    getNodeStats,
    getEdgeStats,
    nodeStats: query.data?.node_stats || [],
    edgeStats: query.data?.edge_stats || [],
  };
}
